import React, { useState, useEffect } from 'react';
import { Layout, Menu, theme, Button, Dropdown, Space, Avatar, ConfigProvider } from 'antd';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  ShopOutlined,
  DatabaseOutlined,
  AreaChartOutlined,
  TeamOutlined,
  UserOutlined,
  KeyOutlined,
  LogoutOutlined,
  DownOutlined,
  EditOutlined,
  AppstoreOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import ChangePasswordModal from '../auth/ChangePasswordModal';
import 'antd/dist/reset.css';

const { Header, Content, Sider } = Layout;

// 导航菜单项定义
type MenuItem = {
  key: string;
  icon?: React.ReactNode;
  label: React.ReactNode;
  children?: MenuItem[];
};

// 创建导航菜单项
function getMenuItems(isAdmin: boolean, systemMode: 'xianyu' | 'xhs'): MenuItem[] {
  const menuItems: MenuItem[] = [
    {
      key: 'competitor',
      icon: <TeamOutlined />,
      label: '对标店铺数据',
      children: [
        {
          key: '/competitor/shop-groups',
          icon: <AppstoreOutlined />,
          label: <Link to="/competitor/shop-groups">店铺分组管理</Link>,
        },
        {
          key: '/competitor/shops',
          icon: <ShopOutlined />,
          label: <Link to="/competitor/shops">店铺管理</Link>,
        },
        {
          key: '/competitor/scrape',
          icon: <DatabaseOutlined />,
          label: <Link to="/competitor/scrape">数据抓取</Link>,
        },
        {
          key: '/competitor/analysis',
          icon: <AreaChartOutlined />,
          label: <Link to="/competitor/analysis">数据分析</Link>,
        },
      ],
    },
    {
      key: 'self',
      icon: <TeamOutlined />,
      label: '自己店铺数据',
      children: [
        {
          key: '/self/shop-groups',
          icon: <AppstoreOutlined />,
          label: <Link to="/self/shop-groups">店铺分组管理</Link>,
        },
        {
          key: '/self/shops',
          icon: <ShopOutlined />,
          label: <Link to="/self/shops">店铺管理</Link>,
        },
        {
          key: '/self/scrape',
          icon: <DatabaseOutlined />,
          label: <Link to="/self/scrape">数据抓取</Link>,
        },
        {
          key: '/self/analysis',
          icon: <AreaChartOutlined />,
          label: <Link to="/self/analysis">数据分析</Link>,
        },
      ],
    },
    {
      key: 'xhs',
      icon: <TeamOutlined />,
      label: '小红书数据',
      children: [
        {
          key: '/xhs/shop-groups',
          icon: <AppstoreOutlined />,
          label: <Link to="/xhs/shop-groups">店铺分组管理</Link>,
        },
        {
          key: '/xhs/shops',
          icon: <ShopOutlined />,
          label: <Link to="/xhs/shops">店铺管理</Link>,
        },
        {
          key: '/xhs/scrape',
          icon: <DatabaseOutlined />,
          label: <Link to="/xhs/scrape">数据抓取</Link>,
        },
        {
          key: '/xhs/analysis',
          icon: <AreaChartOutlined />,
          label: <Link to="/xhs/analysis">数据分析</Link>,
        },
        {
          key: '/xhs/display',
          icon: <LineChartOutlined />,
          label: <Link to="/xhs/display">数据展现</Link>,
        },
      ],
    },
  ];
  
  // 仅管理员可见的菜单项
  if (isAdmin) {
    menuItems.push({
      key: 'admin',
      icon: <KeyOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/admin/invite-codes',
          icon: <KeyOutlined />,
          label: <Link to="/admin/invite-codes">邀请码管理</Link>,
        }
      ],
    });
  }
  
  const filtered = systemMode === 'xhs'
    ? menuItems.filter(m => ['xhs', 'admin'].includes(m.key))
    : menuItems.filter(m => ['competitor', 'self', 'admin'].includes(m.key));
  return filtered;
}

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [systemMode, setSystemMode] = useState<'xianyu' | 'xhs'>(window.location.pathname.startsWith('/xhs') ? 'xhs' : 'xianyu');
  const [openKeys, setOpenKeys] = useState<string[]>(window.location.pathname.startsWith('/xhs') ? ['xhs', 'admin'] : ['competitor', 'self', 'admin']);
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, isAdmin, logout } = useAuth();
  
  // 获取当前路径的导航菜单key
  const getSelectedKeys = () => {
    return [location.pathname];
  };
  
  // 退出登录
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 显示修改密码弹窗
  const handleChangePassword = () => {
    setChangePasswordVisible(true);
  };

  // 关闭修改密码弹窗
  const handleCloseChangePassword = () => {
    setChangePasswordVisible(false);
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  useEffect(() => {
    setSystemMode(location.pathname.startsWith('/xhs') ? 'xhs' : 'xianyu');
  }, [location.pathname]);

  useEffect(() => {
    setOpenKeys(systemMode === 'xhs' ? ['xhs', 'admin'] : ['competitor', 'self', 'admin']);
  }, [systemMode]);

  const systemTitle = systemMode === 'xhs' ? '小红书商品数据采集系统' : '闲鱼商品数据采集系统';
  const systemColor = systemMode === 'xhs' ? '#ff2442' : '#fadb14';

  const onSelectSystem = (key: string) => {
    const mode = key === 'xhs' ? 'xhs' : 'xianyu';
    setSystemMode(mode);
    if (mode === 'xhs') navigate('/xhs/shops');
    else navigate('/competitor/shops');
  };

  const systemMenu = {
    items: [
      { key: 'xianyu', label: '闲鱼商品数据采集系统' },
      { key: 'xhs', label: '小红书商品数据采集系统' },
    ],
    onClick: ({ key }: { key: string }) => onSelectSystem(key),
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: systemColor } }}>
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ padding: 0, background: colorBgContainer, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Dropdown menu={systemMenu} trigger={['click']}>
          <Button type="text" style={{ color: systemColor, fontSize: '20px', fontWeight: 'bold', padding: '0 24px', height: 64 }}>
            <Space>
              {systemTitle}
              <DownOutlined />
            </Space>
          </Button>
        </Dropdown>
        <div style={{ marginRight: '24px' }}>
          <Dropdown menu={{ 
            items: [
              {
                key: 'changePassword',
                icon: <EditOutlined />,
                label: '修改密码',
                onClick: handleChangePassword
              },
              {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: '退出登录',
                onClick: handleLogout
              }
            ] 
          }}>
            <Button type="text">
              <Space>
                <Avatar icon={<UserOutlined />} size="small" />
                {currentUser?.username || '用户'}
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Sider 
          width={200} 
          collapsible 
          collapsed={collapsed} 
          onCollapse={(value) => setCollapsed(value)}
          style={{ background: colorBgContainer }}
        >
          <Menu
            mode="inline"
            selectedKeys={getSelectedKeys()}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            style={{ height: '100%', borderRight: 0 }}
            items={getMenuItems(isAdmin, systemMode)}
          />
        </Sider>
        <Layout style={{ padding: '24px' }}>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
              overflow: 'auto',
            }}
          >
            <Outlet />
          </Content>
        </Layout>
      </Layout>
      
      {/* 修改密码弹窗 */}
      <ChangePasswordModal 
        visible={changePasswordVisible} 
        onCancel={handleCloseChangePassword} 
      />
    </Layout>
    </ConfigProvider>
  );
};

export default MainLayout;
